import { useNavigate } from 'react-router-dom';
import { SelectButton } from 'primereact/selectbutton';
import { Paginator } from '@/shared/ui/Paginator/Paginator';
import type { OperatorTaskListItemResponse } from '@/core/generated/api';
import { useOperatorTasks } from '@/entities/OperatorTask/model/store';
import { RequestStatus } from '@/shared/model/requestStatus';
import { SidePanel } from '@/shared/ui/SidePanel/SidePanel';
import {
    RequestEmpty,
    RequestError,
    RequestErrorBanner,
    RequestLoading,
} from '@/shared/ui/RequestState';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { formatMoscowDateTime } from '@/shared/lib/dateTime';
import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { hasUnsavedChanges } from '@/shared/model/unsavedChanges';
import { useTaskCompletion } from '../model/useTaskCompletion';
import { OperatorTaskCard } from './OperatorTaskCard';
import classes from './TaskNotifications.module.scss';

/** Открывает общую очередь уведомлений операторов */
export const TaskNotifications = () => {
    const navigate = useNavigate();
    const open = useOperatorTasks((state) => state.notificationsOpen);
    const close = useOperatorTasks((state) => state.closeNotifications);
    const view = useOperatorTasks((state) => state.notificationsView);
    const setView = useOperatorTasks((state) => state.setNotificationsView);
    const page = useOperatorTasks((state) => state.notificationsPage);
    const maxPageSize = useOperatorTasks(state => state.options?.listMaxTake ?? 200);
    const pageSize = useOperatorTasks(state => Math.min(state.notificationsPageSize, maxPageSize));
    const setPageSize = useOperatorTasks(state => state.setNotificationsPageSize);
    const setPage = useOperatorTasks((state) => state.setNotificationsPage);
    const attention = useOperatorTasks((state) => state.attention);
    const attentionStatus = useOperatorTasks((state) => state.attentionStatus);
    const attentionError = useOperatorTasks((state) => state.attentionError);
    const feed = useOperatorTasks((state) => state.reminderFeed);
    const feedStatus = useOperatorTasks((state) => state.feedStatus);
    const feedError = useOperatorTasks((state) => state.feedError);

    const updatedAt = useOperatorTasks((state) => state.notificationsView === 'attention'
        ? state.attentionUpdatedAt
        : state.feedUpdatedAt);

    const summaryStatus = useOperatorTasks((state) => state.summaryStatus);
    const summaryError = useOperatorTasks((state) => state.summaryError);
    const invalidate = useOperatorTasks((state) => state.invalidateNotifications);
    const { changeStatus, pending, error } = useTaskCompletion();

    const status = view === 'attention'
        ? attentionStatus
        : feedStatus;

    const loadError = view === 'attention'
        ? attentionError
        : feedError;

    const count = (view === 'attention'
        ? attention?.allCount
        : feed?.allCount) ?? 0;

    const views = [
        {
            id: 'attention',
            name: 'Требуют внимания',
        },
        {
            id: 'all',
            name: 'Все события',
        },
    ];

    /** Открывает задачу из уведомления */
    const openTask = (task: OperatorTaskListItemResponse) => {
        if (!task.id) return;

        confirmUnsavedChanges(hasUnsavedChanges(), () => {
            close();
            navigate(`/operator-tasks?task_id=${task.id}`);
        });
    };

    return (
        <SidePanel
            id="task-notifications-panel"
            visible={open}
            title="Уведомления о задачах"
            onHide={close}
            closable={pending.length === 0}
        >
            <div className={classes.panel}>
                <p>Общая лента</p>
                <SelectButton
                    value={view}
                    options={views}
                    optionValue="id"
                    optionLabel="name"
                    allowEmpty={false}
                    aria-label="Режим уведомлений"
                    onChange={(event) => {
                        if (event.value !== 'all' && event.value !== 'attention') return;

                        setView(event.value);
                        invalidate();
                    }}
                />
                <p className={classes.notice}>Уведомления общие для смены. Просмотр не останавливает повторы</p>
                <div className={classes.refresh}>
                    <small>
                        {updatedAt
                            ? `Обновлено: ${formatMoscowDateTime(updatedAt)}`
                            : 'Данные ещё не обновлены'}
                    </small>
                    <ActionButton
                        label="Обновить"
                        icon="pi pi-refresh"
                        outlined
                        disabled={status === RequestStatus.Loading}
                        onClick={invalidate}
                    />
                </div>
                <RequestErrorBanner
                    status={summaryStatus}
                    error={summaryError}
                    fallbackMessage="Сводка недоступна. Показаны последние загруженные данные"
                    onRetry={invalidate}
                />
                <RequestErrorBanner
                    status={status}
                    error={loadError}
                    fallbackMessage="Не удалось обновить уведомления"
                    onRetry={invalidate}
                />
                {status === RequestStatus.Error &&
                    <p>Показаны последние загруженные данные. Обновите очередь перед выполнением</p>
                }
                {status === RequestStatus.Loading &&
                    <RequestLoading message="Обновление уведомлений..." />
                }
                {error &&
                    <RequestError
                        message={error}
                        compact
                    />
                }
                {view === 'attention' && (
                    <>
                        <h2 className={classes.groupTitle}>Сейчас</h2>
                        {status === RequestStatus.Success && !attention?.items?.length &&
                            <RequestEmpty message="Нет задач, требующих внимания" />
                        }
                        {attention?.items?.map((item) => item.task && (
                            <OperatorTaskCard
                                notification
                                key={item.task.id}
                                task={item.task}
                                reminder={item.latestReminder}
                                busy={Boolean(item.task.id && pending.includes(item.task.id))}
                                disabled={status === RequestStatus.Error || summaryStatus === RequestStatus.Error}
                                onOpen={() => item.task && openTask(item.task)}
                                onComplete={() => item.task && void changeStatus(item.task)}
                            />
                        ))}
                    </>
                )}
                {view === 'all' && (
                    <>
                        <h2 className={classes.groupTitle}>Сохранённые напоминания</h2>
                        {status === RequestStatus.Success && !feed?.items?.length &&
                            <RequestEmpty message="Сохранённых напоминаний пока нет" />
                        }
                        {feed?.items?.map((item) => item.task && (
                            <OperatorTaskCard
                                notification
                                key={item.id}
                                task={item.task}
                                reminder={item}
                                busy={Boolean(item.task.id && pending.includes(item.task.id))}
                                disabled={status === RequestStatus.Error || summaryStatus === RequestStatus.Error}
                                onOpen={() => item.task && openTask(item.task)}
                                onComplete={() => item.task && void changeStatus(item.task)}
                            />
                        ))}
                    </>
                )}
                {(attention || feed) &&
                    <Paginator
                        first={page * pageSize}
                        rows={pageSize}
                        totalRecords={count}
                        rowsPerPageOptions={[10, 20, 50, 100].filter(size => size <= maxPageSize)}
                        disabled={status !== RequestStatus.Success}
                        label="Страницы уведомлений о задачах"
                        onPageChange={(event) => {
                            if (event.rows !== pageSize) setPageSize(event.rows);
                            else setPage(event.page);
                            invalidate();
                        }}
                    />
                }
                {view === 'attention' && Boolean(attention?.upcoming?.length) && (
                    <>
                        <h2 className={classes.groupTitle}>Скоро</h2>
                        {attention?.upcoming?.map((task) => (
                            <OperatorTaskCard
                                notification
                                key={task.id}
                                task={task}
                                upcoming
                                busy={Boolean(task.id && pending.includes(task.id))}
                                disabled={status === RequestStatus.Error || summaryStatus === RequestStatus.Error}
                                onOpen={() => openTask(task)}
                                onComplete={() => void changeStatus(task)}
                            />
                        ))}
                    </>
                )}
                <small>Повторы прекращаются после выполнения задачи</small>
            </div>
        </SidePanel>
    );
};
